import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Route, withRouter } from 'react-router-dom';
import _ from 'lodash';
import { Translate } from 'core/translate';
import { getNamedPath } from '../common/routing/Router';
import FeaturesCompareModal from './FeaturesCompareModal';
import { withModelInfo } from '../common/ModelInfoContext';
import Preload from '../common/Preload';
import MatrixConflictModal from './matrixConflict/MatrixConflictModal';
import OptionDetails from '../views/optionDetails/OptionDetails';
import ScrollTop from '../common/ScrollTop';
import Warning from './warning/Warning';
import {
  updateConfigWithOption,
  updateConfigWithSelections,
  updateConfigWithStyleIdAndSerializedState
} from '../store/actions';

class ConfigPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      optionAfterWarning: null,
      typeAfterWarning: null,
      onChange: this.onChange,
      onTrimFilteringChange: this.onTrimFilteringChange,
      onDetails: this.onDetails
    };
  }

  onTrimFilteringChange = option => {
    const {
      routeName,
      wasOptionToggled,
      wasTrimOptionToggled,
      onTrimFilteringChange
    } = this.props;
    const { styleId, ss } = option;

    if (wasOptionToggled || (routeName === 'CONFIG' && wasTrimOptionToggled)) {
      this.setState({
        optionAfterWarning: option
      });
      return;
    }

    onTrimFilteringChange(styleId, ss, { toggledTrim: routeName === 'TRIM' });
  };

  onTrimFilteringWarningSelect = () => {
    const { routeName, onTrimFilteringChange } = this.props;
    const { optionAfterWarning: option } = this.state;
    const { styleId, ss } = option;

    this.setState({ optionAfterWarning: null }, () => {
      onTrimFilteringChange(styleId, ss, { toggledTrim: routeName === 'TRIM' });
    });
  };

  onChange = (type, option) => {
    const {
      config,
      wasOptionToggled,
      history,
      routeName,
      onChange
    } = this.props;
    const { id, conflict } = option;
    const existingSelections = config.selections;

    if (existingSelections[type] === id) {
      return; // no-op, selection is already selected
    }

    if (wasOptionToggled) {
      this.setState({
        optionAfterWarning: option,
        typeAfterWarning: type
      });
      return;
    }

    if (conflict) {
      const pathname = getNamedPath(routeName);
      return history.push(`${pathname}/conflict/${type}/${id}`);
    }

    onChange(type, option);
  };

  onWarningSelect = () => {
    const { history, routeName, onChange } = this.props;
    const { optionAfterWarning: option, typeAfterWarning: type } = this.state;
    const { id, conflict } = option;

    this.setState(
      {
        optionAfterWarning: null,
        typeAfterWarning: null
      },
      () => {
        if (conflict) {
          const pathname = getNamedPath(routeName);
          return history.push(`${pathname}/conflict/${type}/${id}`);
        }

        onChange(type, option);
      }
    );
  };

  onWarningCancel = () => {
    this.setState({
      optionAfterWarning: null,
      typeAfterWarning: null
    });
  };

  onDetails = option => {
    const { history, routeName } = this.props;
    const pathname = getNamedPath(routeName);
    history.push(`${pathname}/option/${option.id}`);
  };

  onModalClose = () => {
    const { history, routeName } = this.props;
    history.push(getNamedPath(routeName));
  };

  render() {
    const {
      children,
      routeName,
      modelInfo,
      config,
      disclaimer,
      postalCode,
      onConflictSelect,
      onChange
    } = this.props;
    const { isLiteRenderMode, classicModel, enableTrimFiltering } = modelInfo;
    const pathname = getNamedPath(routeName);
    return (
      <ScrollTop>
        {children(this.state)}
        <Preload loader={() => import('../components/features/Features')} />
        <Route
          path={`${pathname}/compare`}
          render={() => (
            <FeaturesCompareModal
              returnUrl={pathname}
              returnText={
                <Translate value="component.trim.compare.return.to.build" />
              }
              config={config}
              disclaimer={disclaimer}
              disableCapabilities={isLiteRenderMode}
              classicModel={classicModel}
              postalCode={postalCode}
            />
          )}
        />
        <Route
          path={`${pathname}/conflict/:type/:id`}
          render={({ match, history }) => {
            const { id, type } = match.params;

            const onSelect = selections => {
              history.push(pathname);
              onConflictSelect(
                _.pick(selections, [
                  'axleRatio',
                  'bodyTypeId',
                  'driveType',
                  'engine',
                  'styleId',
                  'transmission'
                ])
              );
            };

            return (
              <MatrixConflictModal
                classicModel={classicModel}
                config={config}
                type={type}
                id={id}
                onClose={this.onModalClose}
                onSubmit={onSelect}
              />
            );
          }}
        />
        <Route
          path={`${pathname}/option/:id`}
          render={({ match, history }) => {
            const { id } = match.params;
            const option = config.options[id];

            const onSelect = () => {
              const type = 'engine';
              if (option.conflict) {
                return history.push(`${pathname}/conflict/${type}/${id}`);
              }
              history.push(pathname);
              onChange(type, option);
            };

            return (
              <OptionDetails
                option={option}
                onClose={this.onModalClose}
                onSelect={onSelect}
              />
            );
          }}
        />
        {this.state.optionAfterWarning && (
          <Warning
            packages={config.packages}
            onSelect={
              enableTrimFiltering
                ? this.onTrimFilteringWarningSelect
                : this.onWarningSelect
            }
            onClose={this.onWarningCancel}
          />
        )}
      </ScrollTop>
    );
  }
}

ConfigPage.propTypes = {
  routeName: PropTypes.string.isRequired,
  children: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const {
    config,
    disclaimers,
    params,
    wasOptionToggled,
    wasTrimOptionToggled
  } = state;
  const disclaimer = disclaimers.simple;
  const postalCode = params.postalCode;
  return {
    config,
    disclaimer,
    postalCode,
    wasOptionToggled,
    wasTrimOptionToggled
  };
};

const mapDispatchToProps = dispatch => {
  const onChange = (type, option) =>
    dispatch(updateConfigWithOption(type, option));

  const onTrimFilteringChange = (styleId, ss, options) =>
    dispatch(updateConfigWithStyleIdAndSerializedState(styleId, ss, options));

  const onConflictSelect = selections =>
    dispatch(updateConfigWithSelections(selections));

  return {
    onChange,
    onTrimFilteringChange,
    onConflictSelect
  };
};

export default withRouter(
  withModelInfo(connect(mapStateToProps, mapDispatchToProps)(ConfigPage))
);
